import React from 'react';
import { useRouter } from 'next/compat/router';

import useTranslation from 'hooks/use-translation';
import { useOpenDutchiePay } from 'src/payments/hooks/use-open-dutchie-pay/use-open-dutchie-pay';
import useUI from 'src/hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';

import { Typography } from 'src/typography';
import { CheckIcon } from 'src/assets/check';
import { PBB_NAME } from 'src/payments/constants';
import {
  ButtonGroup,
  StyledHeading,
  StyledList,
  StyledListItem,
  StyledSignUpButton,
  StyledTitle,
} from './new-user.styles';

type NewUserProps = {
  allowDutchiePayEnrollment?: boolean;
};

export function NewUser({ allowDutchiePayEnrollment = true }: NewUserProps): JSX.Element {
  const { t } = useTranslation();
  const router = useRouter();
  const { isDutchieMain, variant } = useUI();
  const { dispensary } = useDispensary();
  const enrollmentSource = `my-account-${String(variant)}`;

  const { openDutchiePay } = useOpenDutchiePay({
    cname: !isDutchieMain ? dispensary?.cName : null,
    refHref: router?.asPath,
    enrollmentSource,
  });

  const handleSignUpClick = (): void => {
    openDutchiePay();
  };

  const bullets = [`Simple and easy setup`, `Pay online or in-store`, `Quicker checkouts`];

  return (
    <>
      <StyledHeading tag='h1' size='large'>
        Secure and simple digital payments in your pocket.
      </StyledHeading>

      <Typography.Body size='large'>
        {`${PBB_NAME} enables you to simply link your bank account for fast, secure and cashless payments for cannabis.`}
      </Typography.Body>

      <StyledTitle size='small' casing='uppercase' tag='h2'>
        {`Why Use ${PBB_NAME}?`}
      </StyledTitle>

      <StyledList>
        {bullets.map((bullet) => (
          <StyledListItem key={bullet} tag='li' size='large'>
            <CheckIcon width={20} height={20} />
            {bullet}
          </StyledListItem>
        ))}
      </StyledList>

      {allowDutchiePayEnrollment && (
        <ButtonGroup>
          <StyledSignUpButton onClick={handleSignUpClick} size='large'>
            {t('common.signup', 'Sign Up')}
          </StyledSignUpButton>
        </ButtonGroup>
      )}
    </>
  );
}
